import styles from "./TableCell.module.scss";
import { Fragment, ReactNode, memo, useContext, useEffect, useRef, useState } from "react";
import { classNames, dateParser, getLink, nFormatter } from "@/lib/utils";
import { fmtForecastProbability } from "@/lib/forecast-probability";
import ConsensusCell from "./ConsensusCell";
import dayjs from "dayjs";
import { atom, useAtomValue } from "jotai";
import { positiveState, negativeState, greyLighterState, themeState, ortexColorState, dividerState, subtextGreyState } from "@/lib/store";
import { Column } from "@/models/table";
import { isNil } from "lodash";
import ProgressBar from "@/components/progress-bar/ProgressBar";
import ChartCell from "@/components/chart-cell/ChartCell";
import { Context, ModuleContext } from "@/lib/Context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartColumn } from "@fortawesome/pro-solid-svg-icons";
import Rating from "@mui/material/Rating";
import StarIcon from "@mui/icons-material/Star";
import Tooltip from "@mui/material/Tooltip";
import CellTooltip from "./CellTooltip";
import ReplayIcon from "@mui/icons-material/Replay";
import RestoreIcon from "@mui/icons-material/Restore";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import SizeBar from "../size-bar/SizeBar";
import StackedBar from "../stacked-bar/StackedBar";
import Meter from "../meter/Meter";
import CountryFlag from "@/components/country-flag/CountryFlag";
import Link from "next/link";
import SparkChart from "../spark-chart/SparkChart";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { ArrowDownward } from "@mui/icons-material";
import type { TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";
import { usExchanges } from "@/lib/vars";
import { useImageHandler } from "src/hooks/useImageHandler";
import { TableCellContext, currentPathname } from "./cellContext";
import { UPGRADE_LOCATIONS, trackUpgradeClick } from "@/lib/tracking";
import { useNoBackgroundLogos } from "src/hooks/useNoBackgroundLogos";
import Image from "next/legacy/image";
import { ExtraButton } from "@/models/datagrid";
import { useCompanyLogo, useLogosConfig } from "@/lib/logos";
import { onParsedMessage } from "@/lib/wsDispatch";

// Formats whose value comes from the live socket (constant: was a fresh array per cell per render).
const LIVE_FORMATS = ["live", "live_millify", "live_millify_pct", "spark_chart"];
const LIVE_PRICE_FORMATS = ["live_millify", "live_millify_pct"];

/**
 * The colours a cell draws with, read through one subscription instead of one per colour: a grid's
 * first paint mounts every cell at once, and nine `useAtomValue`s each was 9 x rows x columns
 * subscriptions. A derived atom keeps its last value until an input changes, so the object is stable.
 */
const cellColorsState = atom((get) => ({
    subtextGrey: get(subtextGreyState),
    greyLighter: get(greyLighterState),
    divider: get(dividerState),
    ortex: get(ortexColorState),
    positive: get(positiveState),
    negative: get(negativeState),
}));

/**
 * The company logo of a `company_w_logo` cell. Its hooks (logo config, image self-heal, no-background
 * list, logo URL) used to run in every cell of the grid, logo or not, each with its own effects and a
 * logo-config refresh timer; now only the column that draws a logo pays for them.
 */
const CellLogo = memo(function CellLogo({ row, obfuscated }: { row: any; obfuscated: boolean }) {
    const logosCfg = useLogosConfig(); // re-render when web flips the logo source or rotates the token
    const theme = useAtomValue(themeState);
    const { hasImgError, isPlaceholderImg, handleImgError, handleImgLoad, imgRetryVersion } = useImageHandler("datagrid-table-cell");
    const { logoNoBackground } = useNoBackgroundLogos();
    const { ticker, security_id, securityId } = row;
    // Logo URL, with a cache-buster only after a retry (v=0 → no query
    // param, so the normal URL is byte-identical to before).
    const logoSid = (security_id || securityId) as number | undefined;
    const logoRetry = imgRetryVersion(logoSid);
    const logo = useCompanyLogo(
        {
            isin: (row as any).isin ?? null,
            ortexSid: (row as any).ortexSid ?? (row as any).ortex_sid ?? null,
            ticker,
            exchange: row.exchangeSymbol || row.exchangesymbol || row.exchange || null,
            securityId: logoSid,
        },
        "rect",
        // A strict-theme refusal is not a missing logo: only the last failure feeds the
        // self-heal path (backend trigger, one-shot retry, then the ticker box).
        { retry: logoRetry, onMissing: () => handleImgError(logoSid) }
    );
    return (
        <Box
            className={obfuscated ? classNames(styles.logo, "obfuscated") : styles.logo}
            sx={{
                backgroundColor: logo.plate ?? (logosCfg.source === "edge" ? "transparent" : theme === "dark" && !logoNoBackground(logoSid) && !isPlaceholderImg(logoSid) ? "#c0c0c0" : "transparent"),
                mr: 1,
            }}
        >
            {hasImgError(logoSid) || isPlaceholderImg(logoSid) ? (
                <Box
                    className={styles.imgErrorPlaceholder}
                    aria-hidden="true"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: 0.4,
                        // As before the split: the obfuscated cell's monogram uses the text token, the open one its own grey.
                        color: obfuscated ? "var(--text-2, #a1a6ad)" : "rgba(120, 130, 140, 0.9)",
                        overflow: "hidden",
                    }}
                >
                    {String(ticker ?? "").slice(0, 5)}
                </Box>
            ) : (
                <Image unoptimized src={logo.src} alt={`logo`} layout="fill" objectFit="contain" onError={logo.onError} onLoad={logo.onLoad} onLoadingComplete={(img) => handleImgLoad(img, logoSid)} />
            )}
        </Box>
    );
});

type Props = {
    button: {
        icon?: ReactNode;
        /** Per-row icon. Takes precedence over `icon` and the `series` color logic; see useChartSeriesToggle. */
        renderIcon?: (value: any, row: any) => ReactNode;
        onClick: (filter: number | string, row: object) => any;
        series?: (Partial<TimeSeries> & { row_id?: string })[];
        rowKey?: string;
    };
    datetime: boolean;
    header: Column;
    link?: string;
    liveData: {
        message: (row: (string | number)[]) => any;
        onMessage: (data: any, name: string, row: any, setLivePrice?: (value: Array<number | string>) => void) => number | string | void;
        initialValue?: (row: any) => string | number | null;
        websocket?: WebSocket;
    }[];
    onRowClick?: (row: any) => any | void;
    row: any;
    value: any;
    tooltip?: string;
    extraButton?: ExtraButton | ExtraButton[];
    className?: string;
    last_close?: boolean;
    updateRowSubscriptionData?: (ticker: any, data: any) => void;
    getRowSubscriptionData?: (ticker: any) => {
        price: number | null;
        unsubscribed: boolean;
    };
    rowNote?: boolean;
    noPrefixDate?: boolean;
    wrapStockCell?: (row: any, cell: ReactNode, stockHref: string) => ReactNode;
};

const TableCell = ({ button, datetime, header, liveData, onRowClick, row, value, tooltip, extraButton, className, last_close, updateRowSubscriptionData, getRowSubscriptionData, rowNote, noPrefixDate, wrapStockCell }: Props) => {
    const { obfuscated } = useContext(ModuleContext);
    const { ortexWebSocket } = useContext(Context);
    const { openSubscriptionForm } = useContext(TableCellContext);

    // Every obfuscated cell in this component opens the payment form; route them
    // all through one handler so the click is recorded exactly once per press.
    const openSubscriptionFormTracked = () => {
        trackUpgradeClick(UPGRADE_LOCATIONS.TABLE_CELL, { format: header?.format });
        openSubscriptionForm();
    };

    const { subtextGrey, greyLighter, divider: dividerColor, ortex: ortexColor, positive, negative } = useAtomValue(cellColorsState);
    const wsRef = useRef(false);
    const pathname = currentPathname();

    const [data, setData] = useState(() => {
        if (liveData && LIVE_FORMATS.includes(header.format)) {
            for (const obj of liveData) {
                if (obj.initialValue) {
                    const initial = obj.initialValue(row);
                    if (initial !== null && initial !== undefined) return [String(initial)];
                }
            }
        }
        return value;
    });
    const [livePrice, setLivePrice] = useState<(string | number)[]>(() => {
        if (liveData && LIVE_PRICE_FORMATS.includes(header.format)) {
            for (const obj of liveData) {
                if (obj.initialValue) {
                    const initial = obj.initialValue(row);
                    if (initial !== null && initial !== undefined) return [String(initial)];
                }
            }
        }
        return [];
    });
    const isLiveCell = liveData && LIVE_FORMATS.includes(header.format);

    const isObfuscated = className === "obfuscated";

    const getCellComponent = (header: Column, row, cellValue) => {
        if (header.onClick === "link" && header.link !== undefined && !className)
            return (
                <Link passHref legacyBehavior href={getLink(row, header.link)}>
                    {cellValue}
                </Link>
            );
        if (isObfuscated && header.onClick === "link" && header.link !== undefined) {
            return (
                <div onClick={openSubscriptionFormTracked} style={{ cursor: "pointer" }}>
                    {cellValue}
                </div>
            );
        }
        return cellValue;
    };

    useEffect(() => {
        // WebSocket connection
        try {
            const callbacks: Record<number, () => void> = {};

            const currentWS = ortexWebSocket;

            if (!isLiveCell) return;
            // If wsStatus is true, it means that the row was unsubscribed and we need to subscribe again

            const wsStatus = getRowSubscriptionData ? getRowSubscriptionData(row.ticker)?.unsubscribed : false;
            if (wsStatus) {
                if (currentWS) {
                    if (usExchanges.includes(row.exchangeSymbol || row.exchangesymbol || row.exchange)) {
                        currentWS.send(JSON.stringify({ type: "subscribe", identifier: row.ticker }));
                    } else {
                        currentWS.send(JSON.stringify({ type: "subscribe", identifier: (row.isin || row["peer_isin"]) + (row.currency || row.currency_iso || row.peerCurrency || row["peer_currency"]) }));
                    }
                    updateRowSubscriptionData?.(row.ticker, { unsubscribed: false });
                }
            }

            // If not, it means that the row was already subscribed and we don't need to subscribe again.
            // The frame is parsed once per socket (wsDispatch); this cell only checks whether it is its own.
            liveData.forEach((obj, i) => {
                const ws = obj.websocket || currentWS;
                if (!ws) return;
                callbacks[i] = onParsedMessage(ws, (parsedData) => {
                    const res = obj.onMessage(parsedData, header.headerName || header.id, row, setLivePrice);
                    res && setData([res]);
                });
            });

            return () => {
                if (!isLiveCell) return;
                liveData.forEach((obj, i) => {
                    callbacks[i]?.();
                    if (obj.websocket) {
                        // Grids that pass their own socket (/currencies, /commodities) subscribe by ISIN, so their rows have no
                        // ticker; an unsubscribe without one names no instrument and the server ignores it.
                        if (obj.websocket?.readyState === WebSocket.OPEN && wsRef.current && row.ticker) {
                            obj.websocket.send(JSON.stringify({ type: "unsubscribe", identifier: row.ticker }));
                            updateRowSubscriptionData?.(row.ticker, { unsubscribed: true });
                        }
                    } else {
                        if (currentWS?.readyState === WebSocket.OPEN && wsRef.current) {
                            if (usExchanges.includes(row.exchangeSymbol || row.exchangesymbol || row.exchange)) {
                                currentWS.send(JSON.stringify({ type: "unsubscribe", identifier: row.ticker }));
                            } else if ((row.isin || row["peer_isin"]) && (row.currency || row.currency_iso || row.peerCurrency || row["peer_currency"])) {
                                currentWS.send(JSON.stringify({ type: "unsubscribe", identifier: (row.isin || row["peer_isin"]) + (row.currency || row.currency_iso || row.peerCurrency || row["peer_currency"]) }));
                            }
                            updateRowSubscriptionData?.(row.ticker, { unsubscribed: true });
                        }
                    }
                });
            };
        } catch (error) {
            console.error("Error with WS ", error);
        }
        // ortexWebSocket: cells of a grid that passes no socket use the context one, which is replaced on reconnect.
    }, [...(liveData || []).map((obj) => obj.websocket), ortexWebSocket, row]);

    useEffect(() => {
        if ((isLiveCell && livePrice.length > 0 && header.format === "live_millify") || header.format === "live_millify_pct") updateRowSubscriptionData?.(ticker, { price: livePrice[0] });
    }, [livePrice]);

    useEffect(() => {
        // Only a live cell unsubscribes on unmount (wsRef guards that): no timer for the others.
        if (!isLiveCell) return;
        const timeout: NodeJS.Timeout = setTimeout(() => {
            wsRef.current = true;
        }, 1000);
        return () => clearTimeout(timeout);
    }, []);

    // Keep the rendered value in sync when the cell's value prop changes (e.g. a new column is added
    // and the rows are refetched). Live cells are driven by WebSocket updates, so skip them.
    useEffect(() => {
        if (!isLiveCell) setData(value);
        // value is a fresh array each render, so depend on its content to avoid re-running every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(value)]);

    const classes = [];
    let formatted = [...data];
    let tooltipText: string;
    let hasColor = false;
    let image: string;

    if ("tooltip" in header) {
        tooltipText = formatted[formatted.length - 1];
        formatted.pop();
    }

    if ("hasTooltip" in header && tooltip) tooltipText = tooltip;

    const { link } = header;
    const company = header.field;
    const { ticker, status } = row;
    const inactiveStatus = status === "inactive";
    // A locked row (a forecast behind an add-on, e.g. Index Rebalance) names no stock: nothing to link to.
    const noStockLink = inactiveStatus || row?.locked === true;
    const styledDiv = link !== undefined && !inactiveStatus ? { cursor: "pointer" } : {};

    const defaultComponent = (
        <div style={styledDiv} className={className}>
            {inactiveStatus ? <Chip label={<Typography variant="captionSessionChip">Inactive</Typography>} color="error" variant="outlined" size="small" sx={{ fontSize: "0.75rem" }} /> : <p style={{ color: subtextGrey }}>{ticker}</p>}
            <p style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span className={!inactiveStatus && ticker ? styles.companyName : undefined}>{row[company]}</span>
                {rowNote && (
                    <svg viewBox="0 0 512 512" width="16" height="16" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                        <path fill="#ED2F2F" d="M256 32l224 400H32L256 32z" stroke="#ED2F2F" strokeWidth="32" strokeLinejoin="round" />
                        <path fill="#FFFFFF" d="M256 148c13.255 0 24 10.745 24 24v120c0 13.255-10.745 24-24 24s-24-10.745-24-24V172c0-13.255 10.745-24 24-24zm0 216c13.255 0 24 10.745 24 24s-10.745 24-24 24-24-10.745-24-24 10.745-24 24-24z" />
                    </svg>
                )}
            </p>
        </div>
    );

    switch (header.format) {
        case "size_bar":
            return <SizeBar percentage={data[0]} max={header.max || 100} />;
        case "stacked_bar":
            return <StackedBar percentage={data[0] || 0} />;
        case "starrating": {
            // A null score is "not rated", which is a different fact from zero
            // and must render as such. It used to call `.toFixed` on the null
            // and take the whole page down with it: the insider Alpha grid
            // shows the buyer's score, most buyers have no score yet, and
            // every one of those rows crashed /insiders?tab=alpha.
            const score = typeof data[0] === "number" && isFinite(data[0]) ? data[0] : null;
            if (score === null) {
                return (
                    <Tooltip arrow title="Not rated">
                        <Box className={className}>—</Box>
                    </Tooltip>
                );
            }
            return (
                <CellTooltip arrow title={!className && score.toFixed(2)}>
                    <Box
                        sx={{
                            "& .MuiRating-root": {
                                display: "flex",
                            },
                        }}
                        className={className}
                    >
                        <Rating value={score / 20} readOnly size="small" className={styles.rating} precision={0.1} emptyIcon={<StarIcon style={{ color: greyLighter }} fontSize="inherit" />} />
                    </Box>
                </CellTooltip>
            );
        }
        case "spark_chart":
            const [liveData, triggerLevel] = livePrice as [number[][], number];

            return <SparkChart data={liveData || []} triggerLevel={triggerLevel || 0} />;
        case "progress_bar":
            return <ProgressBar max={header.max} percentage={data[0]} tooltip={(data[1] || data[0])?.toFixed(2)} className={className} label={header.headerName} />;
        case "time_series":
            return <ChartCell data={data[0]} datetime={datetime} obfuscated={className} />;
        case "multi_time_series":
            return <ChartCell data={data[0]} isMultiTime datetime={datetime} direction={row.direction[0]} />;
        case "gauge":
            return (
                <div style={{ width: "70%" }} className={className}>
                    <Meter value={data} maxValue="100" />
                </div>
            );
        case "button":
            let iconColor: string = "#31aba6";
            if (button && button.series) {
                if (button?.series && row && button.rowKey) iconColor = button.series.some((item) => item?.title?.includes(row[button.rowKey]) || item?.id?.includes(row[button.rowKey])) ? "#31aba6" : "grey";
                else iconColor = button?.series.some((item) => item.id !== undefined && item.row_id === row.id) ? "#31aba6" : "grey";
            }

            return (
                <Button
                    variant={"text"}
                    aria-label={header?.headerName || "Show on chart"}
                    sx={{ paddingX: 0, paddingY: 2 }}
                    className={classNames(styles.Button, className)}
                    onClick={obfuscated || className ? undefined : () => button?.onClick(data[0] || row?.peer_id, row)}
                >
                    {button?.renderIcon ? button.renderIcon(data[0], row) : button?.icon || <FontAwesomeIcon icon={faChartColumn} height={25} color={iconColor} />}
                </Button>
            );
        case "extra-button":
            // For extra-button format, extraButton should be a single button
            const singleExtraButton = Array.isArray(extraButton) ? extraButton[0] : extraButton;

            const onClick = (e) => {
                if (obfuscated || className) {
                    return;
                } else {
                    if (singleExtraButton?.label === "Delete Alert") {
                        singleExtraButton?.onClick(row.id, row);
                    } else if (singleExtraButton?.label === "Edit Alert") {
                        singleExtraButton?.onClick(row, null);
                    } else {
                        singleExtraButton?.onClick(data[0] || row?.peer_id, row, e);
                    }
                }
            };

            const isMuiColor = singleExtraButton?.color && ["primary", "secondary", "error", "warning", "info", "success"].includes(singleExtraButton.color);

            return singleExtraButton?.label === "Reset Alert" || singleExtraButton?.label === "Renew Alert" ? null : (
                <CellTooltip title={isObfuscated ? "" : singleExtraButton?.tooltip} arrow describeChild>
                    <span>
                        <Button
                            variant={"text"}
                            // Icon only: the short label is the name, and the (often longer) tooltip describes it.
                            aria-label={singleExtraButton?.label || singleExtraButton?.tooltip || header?.headerName || "Action"}
                            color={isMuiColor ? singleExtraButton.color : undefined}
                            sx={{
                                paddingX: 0,
                                paddingY: 2,
                                "&:hover": {
                                    backgroundColor: "transparent !important",
                                },
                                ...(!isMuiColor && { color: singleExtraButton?.color || ortexColor }),
                                "&:disabled": {
                                    color: dividerColor,
                                },
                            }}
                            className={classNames(styles.Button, className)}
                            onClick={onClick}
                            disabled={
                                singleExtraButton && "enableWhen" in singleExtraButton
                                    ? !row[singleExtraButton.enableWhen.rowField]
                                    : singleExtraButton?.checkIfShouldShowExtraButton
                                      ? row.status !== "pending" || row.company_type_id === null
                                      : row.company_type_id === null
                            }
                        >
                            {singleExtraButton?.checkIfShouldShowExtraButton
                                ? row.status === "pending" && (singleExtraButton?.icon || <FontAwesomeIcon icon={faChartColumn} height={25} color="#31aba6" />)
                                : singleExtraButton?.icon || <FontAwesomeIcon icon={faChartColumn} height={25} color="#31aba6" />}
                        </Button>
                    </span>
                </CellTooltip>
            );

        case "company":
            if (link !== undefined && !noStockLink) {
                if (isObfuscated) {
                    return (
                        <div onClick={openSubscriptionFormTracked} style={{ cursor: "pointer" }}>
                            {defaultComponent}
                        </div>
                    );
                }
                return (
                    <Link passHref legacyBehavior href={getLink(row, link)}>
                        {defaultComponent}
                    </Link>
                );
            }

            return defaultComponent;

        case "insider": {
            // Links to the insider's profile page. Transaction and position rows
            // carry `insiderSlug`; the stock page's people grid carries `slug`.
            const slug = row?.insiderSlug || row?.slug;
            if (slug && !isObfuscated) {
                return (
                    <Link href={`/insiders/people/${slug}`} className={classNames(styles.TableCell, className)} onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer", color: "inherit", textDecoration: "none" }}>
                        {formatted[0]}
                    </Link>
                );
            }

            break;
        }

        case "politician":
            // Links to the politician detail page when the row carries a
            // bioguide ID (congress filers); executive-branch filers have
            // none and fall through to the plain-text rendering below.
            if (row?.filerMember && !isObfuscated) {
                return (
                    <Link href={`/us-gov-trades/politicians/${row.filerMember}`} className={classNames(styles.TableCell, className)} style={{ cursor: "pointer", color: "inherit", textDecoration: "none" }}>
                        {formatted[0]}
                    </Link>
                );
            }

            break;

        case "activist_filer":
            // Links to the activist-filer profile page when the row carries
            // the reporting person's SEC CIK; group filings without one fall
            // through to plain text.
            if (row?.filerCik && !isObfuscated) {
                return (
                    <Link href={`/activist-filer/${row.filerCik}`} className={classNames(styles.TableCell, className)} style={{ cursor: "pointer", color: "inherit", textDecoration: "none" }}>
                        {formatted[0]}
                    </Link>
                );
            }

            break;

        case "extlink":
            // The cell value IS an external URL (e.g. the filing on sec.gov).
            // A plain click opens it in the in-app SecFilingModal; the href is
            // kept so middle-click/ctrl-click still opens a new tab.
            if (formatted[0]) {
                return (
                    <a
                        className={classNames(styles.TableCell, className)}
                        href={formatted[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ cursor: "pointer", color: "var(--accent-text, #31aba6)", textDecoration: "underline" }}
                        onClick={(e) => {
                            if (e.ctrlKey || e.metaKey || e.shiftKey) return;
                            e.preventDefault();
                            window.dispatchEvent(
                                new CustomEvent("ortex:open-sec-doc", {
                                    detail: { url: formatted[0], docId: row?.id, title: row?.filerName ? `${row.filerName}: SEC Filing` : "SEC Filing" },
                                })
                            );
                        }}
                    >
                        View filing
                    </a>
                );
            }

            break;

        case "external_link": {
            // The cell value is a label; the destination is the row's `<field>Href`,
            // e.g. `sourceOrganization` links to `sourceOrganizationHref` (the
            // publisher's release page on the Alt Data grids). Unlike `extlink` it
            // opens a new tab and nothing else, and a row with no href stays text.
            const href = row?.[`${header.field}Href`];
            if (formatted[0] && href && !isObfuscated) {
                return (
                    <a
                        className={classNames(styles.TableCell, className)}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: "pointer", color: "var(--accent-text, #31aba6)", textDecoration: "underline" }}
                    >
                        {formatted[0]}
                    </a>
                );
            }

            break;
        }

        case "company_w_logo":
            if (link !== undefined && !noStockLink) {
                if (isObfuscated) {
                    return (
                        <Box onClick={openSubscriptionFormTracked} className={styles.stockContainer} sx={{ cursor: "pointer" }}>
                            <CellLogo row={row} obfuscated />
                            {defaultComponent}
                        </Box>
                    );
                }
                const stockCell = (
                    <Box className={styles.stockContainer}>
                        <CellLogo row={row} obfuscated={false} />
                        {defaultComponent}
                    </Box>
                );
                const stockHref = getLink(row, link);
                if (wrapStockCell) return <>{wrapStockCell(row, stockCell, stockHref)}</>;
                return (
                    <Link passHref legacyBehavior href={stockHref}>
                        {stockCell}
                    </Link>
                );
            }

            return defaultComponent;

        case "stock_list":
            classes.push(styles.row);
            return (
                <div className={className}>
                    {formatted.map((stock) => {
                        if (isObfuscated) {
                            return (
                                <CellTooltip title={!className && stock.name} arrow placement="bottom-start" key={stock.ticker}>
                                    <Chip size="small" label={stock.ticker} variant="outlined" sx={{ m: 0.25, cursor: "pointer" }} onClick={openSubscriptionFormTracked} />
                                </CellTooltip>
                            );
                        }
                        return (
                            <Link passHref legacyBehavior href={getLink(stock, "")} key={stock.name}>
                                <CellTooltip title={!className && stock.name} arrow placement="bottom-start" key={stock.ticker}>
                                    <Chip size="small" label={stock.ticker} variant="outlined" sx={{ m: 0.25 }} />
                                </CellTooltip>
                            </Link>
                        );
                    })}
                </div>
            );
        case "date":
            if (row.format_override === "datetime") {
                formatted[0] = formatted[0] ? dateParser(dayjs(formatted[0]), "D MMM YYYY [at] HH:mm", undefined, last_close, undefined, noPrefixDate) : "N/A";
                classes.push(styles.left);
            } else {
                formatted[0] = formatted[0] ? dateParser(dayjs.utc(formatted[0]), "D MMM YYYY", "UTC", last_close, undefined, noPrefixDate) : "N/A";
                // An estimated date (the row says `<field>Estimated: true`) reads "~2 Dec 2026".
                if (formatted[0] !== "N/A" && row?.[`${header.field}Estimated`] === true) formatted[0] = `~${formatted[0]}`;
                classes.push(styles.left);
            }
            break;
        case "datetime":
            if (row.format_override === "date") {
                formatted[0] = formatted[0] ? dateParser(dayjs.utc(formatted[0]), "D MMM YYYY", "UTC", last_close, undefined, noPrefixDate) : "N/A";
                classes.push(styles.left, styles.nowrap);
            } else {
                formatted[0] = formatted[0] ? dateParser(dayjs(formatted[0]), "D MMM YYYY [at] HH:mm", undefined, last_close, undefined, noPrefixDate) : "N/A";
                classes.push(styles.left);
            }
            break;
        case "millify":
            const splittedPathname = pathname.split("/");
            const isIbkrPage = splittedPathname[splittedPathname.length - 1] === "short-interest" && (splittedPathname[1] === "s" || splittedPathname[1] === "id");
            for (let i in formatted) formatted[i] = nFormatter(parseFloat(formatted[i]), 2);
            if (formatted[0] === "-0") formatted[0] = "0";
            if (formatted[0] === "-1" && isIbkrPage) formatted[0] = "> 10m";
            if (formatted.length < 2) formatted[0] = formatted[0].split(/(^-?[0-9]+)/);
            if (Array.isArray(formatted[0]) && formatted[0].length > 1) formatted[0].shift();
            classes.push(styles.nowrap, styles[formatted.length > 1 ? "subtext" : "center"], styles[Array.isArray(formatted[0]) && formatted[0].length > 1 && "millify"]);
            break;
        case "live_millify":
            const [liveMillify] = livePrice;
            formatted[0] = liveMillify ?? (getRowSubscriptionData?.(row.ticker).price || "N/A");
            classes.push(styles.right, styles.nowrap);
            break;
        case "probability":
            // Index-rebalance forecasts: 0.62 -> "62%", 1.0 -> "Confirmed". The value stays numeric
            // in the row, so the column still sorts and filters by number.
            formatted[0] = fmtForecastProbability(formatted[0]) ?? "";
            classes.push(styles.center, styles.nowrap);
            break;
        case "millify_pct":
            if (isFinite(parseFloat(formatted[0]))) {
                classes.push(styles.millify);
                formatted[0] = nFormatter(parseFloat(formatted[0]), 2);
                if (formatted[0] === "-0") formatted[0] = "0";
                formatted[0] = `${formatted[0]}‎‏‏‎ ‎%`.split(/(^-?[0-9]+)/);
                formatted[0].shift();
            } else formatted[0] = "N/A";
            classes.push(styles.left, styles.nowrap);
            break;
        case "call_put": {
            // Options-platform flow: CALL/PUT rendered in the side's
            // colour — instant read of tape direction without icons.
            // Call = ORTEX brand accent, put = the user's negative atom, so
            // both track the app palette instead of hard-coded hex.
            const cpRaw = String(formatted[0] ?? "").toUpperCase();
            if (!cpRaw) break;
            const isCall = cpRaw.startsWith("C");
            return (
                <p className={styles.centeredCells} style={{ color: isCall ? ortexColor : negative, fontWeight: 700, letterSpacing: 0.6, margin: 0 }}>
                    {isCall ? "CALL" : "PUT"}
                </p>
            );
        }
        case "flow_reason": {
            // Outlined chip coloured by qualification reason: Sweep amber
            // (aggressive multi-exchange), Block cyan (negotiated size),
            // anything else neutral grey. Professional — no iconography.
            const reasonVal = String(formatted[0] ?? "");
            if (!reasonVal) break;
            const rl = reasonVal.toLowerCase();
            const reasonColor = rl === "sweep" ? "#F59E0B" : rl === "block" ? "#0891B2" : "rgba(148, 163, 178, 0.9)";
            return (
                <p
                    style={{
                        margin: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "1px 10px",
                        borderRadius: 999,
                        border: `1px solid ${reasonColor}`,
                        color: reasonColor,
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        whiteSpace: "nowrap",
                    }}
                >
                    {reasonVal}
                </p>
            );
        }
        case "millify_arrow":
            const value = parseFloat(formatted[0]).toFixed(2);
            return (
                <p className={classNames(styles["millify_arrow"], styles.centeredCells)}>
                    {value}
                    {row.above ? <ArrowUpwardIcon /> : <ArrowDownward />}
                </p>
            );
        case "live_millify_pct":
            const [liveMillifyPct] = livePrice;
            formatted[0] = liveMillifyPct ?? (getRowSubscriptionData?.(row.ticker).price || "N/A");

            if (formatted[0] !== "N/A" && typeof formatted[0] === "string" && !formatted[0].endsWith("%")) formatted[0] += " %";
            break;
        case "currency":
            formatted[0] = nFormatter(parseFloat(formatted[0]), 2).split(/(^-?[0-9]+)/);
            if (formatted[0].length > 1) formatted[0].shift();
            classes.push(styles.center, styles.nowrap, styles[formatted[0].length > 1 && "millify"]);
            break;
        case "arrow_str":
            if (formatted[0] !== "") image = formatted.shift()?.toLowerCase();
            break;
        case "consensus":
            if (isObfuscated) break;
            return <ConsensusCell data={data as [number | null, number | null][]} tooltip={!obfuscated && !className} onClick={header.onClick !== "link" && onRowClick && !className ? () => onRowClick(row) : undefined} />;
        case "dec2":
            for (let i in formatted) {
                formatted[i] === null ? (formatted[i] = "N/A") : formatted[i]?.toString().split(".").length > 1 ? (formatted[i] = formatted[i]?.toFixed(2)) : formatted[i];
            }
            classes.push(styles[formatted?.length > 1 ? "subtext" : "center"]);
            break;
        case "money":
            for (let i in formatted) {
                formatted[i] === null ? (formatted[i] = "N/A") : (formatted[i] = formatted[i]?.toFixed(2));
                if (formatted[i]?.split(".")[1] === "00") formatted[i] = formatted[i].split(".")[0];
                if (formatted[i]?.toString().length > 3) {
                    formatted[i] = formatted[i]?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                }
            }
            classes.push(styles[formatted?.length > 1 ? "subtext" : "center"]);
            break;
        case "check":
            // A yes/no flag: a tick when true, nothing when false, so the yeses stand out down the column.
            if (isObfuscated) break;
            return (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                    }}
                >
                    {data[0] === true || data[0] === "true" ? <CheckRoundedIcon fontSize="small" sx={{ color: positive }} titleAccess="Yes" /> : null}
                </Box>
            );
        case "str_flag":
            return (
                <>
                    <CountryFlag
                        countryCode={data[0]?.[1]}
                        decorative
                        style={{
                            marginRight: "5px",
                        }}
                    />
                    <p>{data[0]?.[0]}</p>
                </>
            );
    }

    if (!pathname.includes("/screening")) {
        switch (formatted[0]?.toLowerCase?.()) {
            case "buy":
                classes.push(styles.bold, styles.center, styles["big-text"], styles.direction, "positive");
                break;
            case "sell":
            case "sell (partial)":
                classes.push(styles.bold, styles.center, styles["big-text"], styles.direction, "negative");
                break;
        }
    }

    if (header.id === "isocode") classes.push(styles.center, styles["big-text"]);

    if (("class_positive" in header && "class_negative" in header) || ("classPositive" in header && "classNegative" in header)) {
        hasColor = !isNil(data[0]) || (isLiveCell && !isNil(getRowSubscriptionData?.(row.ticker).price));
    }

    return (
        <div className={classNames(styles.TableCell, className)} style={{ margin: header.hasSubtext ? "auto" : "inherit" }}>
            {image && (
                <div className={styles.arrow}>
                    {image === "up" ? (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42.67 64">
                            <path fill={positive} d="M19.57.78L.78 19.5a2.67 2.67 0 003.77 3.78L18.67 9.21v52.12a2.67 2.67 0 105.33 0V9l14.11 14.27a2.67 2.67 0 103.78-3.76L23.35.79a2.67 2.67 0 00-3.78 0z" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42.67 64">
                            <path
                                fill={negative}
                                d="M23.1 63.22L41.88 44.5a2.67 2.67 0 10-3.76-3.78L24 54.79V2.67a2.67 2.67 0 10-5.33 0V55L4.56 40.73a2.67 2.67 0 00-3.77 0A2.63 2.63 0 000 42.61a2.68 2.68 0 00.77 1.88l18.55 18.72a2.67 2.67 0 003.78 0z"
                            />
                        </svg>
                    )}
                </div>
            )}
            <div className={classNames(...classes, styles.container, hasColor && (data[0] < 0 ? "negative" : "positive"), header.link ? styles.link : null)}>
                {formatted.map((value, i) => {
                    const cellValue = Array.isArray(value);
                    const onClick = header.onClick !== "link" && onRowClick && !className ? { onClick: () => onRowClick(row) } : {};

                    const cell = (
                        <CellTooltip arrow title={(i && !tooltip) || obfuscated || className ? undefined : tooltipText}>
                            <div key={i} {...onClick} className={className} style={{ cursor: header.onClick || onRowClick ? "pointer" : "initial" }}>
                                {cellValue ? (
                                    value.map((text, j) => <p key={j}>{text}</p>)
                                ) : header?.id?.includes("description") ? (
                                    <p dangerouslySetInnerHTML={{ __html: value }} className={styles[value.length > 100 && "description"]}></p>
                                ) : header?.field === "status" && (row?.status === "Triggered" || row?.aboutToExpire) ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <p>{value?.toString()}</p>
                                        {row?.status === "Triggered" && Array.isArray(extraButton) && extraButton.find((btn) => btn.label === "Reset Alert") && (
                                            <Tooltip title="This alert has already triggered, reset the alert to allow it to trigger again" arrow>
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => {
                                                        const resetBtn = extraButton.find((btn) => btn.label === "Reset Alert");
                                                        resetBtn?.onClick(row, null);
                                                    }}
                                                >
                                                    <ReplayIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {row?.aboutToExpire && Array.isArray(extraButton) && extraButton.find((btn) => btn.label === "Renew Alert") && (
                                            <Tooltip title="This alert is about to expire in the ten following days. Click to renew it" arrow>
                                                <IconButton
                                                    color="warning"
                                                    onClick={() => {
                                                        const renewBtn = extraButton.find((btn) => btn.label === "Renew Alert");
                                                        renewBtn?.onClick(row, null);
                                                    }}
                                                >
                                                    <RestoreIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                ) : (
                                    <p>{value?.toString()}</p>
                                )}
                            </div>
                        </CellTooltip>
                    );
                    return <Fragment key={i}>{getCellComponent(header, row, cell)}</Fragment>;
                })}
            </div>
        </div>
    );
};

// DataGrid's renderCell builds `value` (and a Status cell's `extraButton`) as a fresh array on every
// call, so plain `memo` never matched and every cell re-rendered with its row. Same items: same cell.
const sameItems = (a: unknown, b: unknown) => a === b || (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item, i) => item === b[i]));

const tableCellPropsEqual = (prev: Props, next: Props) => {
    const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
    for (const key of keys) {
        if (key === "value" || key === "extraButton" ? !sameItems(prev[key], next[key]) : prev[key] !== next[key]) return false;
    }
    return true;
};

export default memo(TableCell, tableCellPropsEqual);
